'use client';

import React, { useState, useEffect } from 'react';
import ChatScreen from './components/ChatScreen';
import ChatList from './components/ChatList';
import ProfileScreen from './components/ProfileScreen';
import SettingsScreen from './components/SettingsScreen';
import SearchScreen from './components/SearchScreen';
import NewConversationScreen from './components/NewConversationScreen';
import AuthScreen from './components/AuthScreen';
import ContactsScreen from './components/ContactsScreen';
import UserProfileScreen from './components/UserProfileScreen';
import NotificationsScreen from './components/NotificationsScreen';
import { useAuth } from '@/contexts/AuthContext';

const CONVERSATIONS: { id: string; name: string }[] = [
  { id: '1', name: 'Ate Boni' },
  { id: '2', name: 'Design Team' },
  { id: '3', name: 'Marco Reyes' },
  { id: '4', name: 'Jess & Friends' },
  { id: '5', name: 'Kuya Dan' },
  { id: '6', name: 'Ana Santos' },
  { id: '7', name: 'Work Updates' },
  { id: '8', name: 'Lola Nena' },
  { id: '9', name: 'Barkada GC' },
  { id: '10', name: 'Paolo Cruz' },
];

type View = 'list' | 'chat' | 'profile' | 'settings' | 'search' | 'new-conversation' | 'contacts' | 'my-profile' | 'notifications';

export default function Page() {
  const [view, setView] = useState<View>('list');
  const [settingsFrom, setSettingsFrom] = useState<'list' | 'chat'>('list');
  const [selectedChatId, setSelectedChatId] = useState<string>('1');
  const { user, loading } = useAuth();

  // Reset to list view when user logs in
  useEffect(() => {
    if (user) {
      setView('list');
    }
  }, [user]);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--background)' }}
      >
        <svg className="animate-spin" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2.5">
          <path d="M21 12a9 9 0 1 1-6.219-8.56" strokeLinecap="round"/>
        </svg>
      </div>
    );
  }

  if (!user) {
    return <AuthScreen onAuthSuccess={() => setView('list')} />;
  }

  if (view === 'my-profile') {
    return <div className="h-screen w-full"><UserProfileScreen onBack={() => setView('list')} /></div>;
  }

  if (view === 'notifications') {
    return <div className="h-screen w-full"><NotificationsScreen onBack={() => setView('list')} onOpenChat={() => setView('chat')} /></div>;
  }

  if (view === 'profile') {
    return <div className="h-screen w-full"><ProfileScreen onBack={() => setView('chat')} /></div>;
  }

  if (view === 'settings') {
    return <div className="h-screen w-full"><SettingsScreen onBack={() => setView(settingsFrom)} /></div>;
  }

  if (view === 'search') {
    return <div className="h-screen w-full"><SearchScreen onBack={() => setView('list')} /></div>;
  }

  if (view === 'new-conversation') {
    return (
      <div className="h-screen w-full overflow-hidden">
        <NewConversationScreen
          onBack={() => setView('list')}
          onStartChat={() => setView('chat')}
        />
      </div>
    );
  }

  if (view === 'contacts') {
    return (
      <div className="h-screen w-full overflow-hidden">
        <ContactsScreen
          onBack={() => setView('list')}
          onMessage={() => setView('chat')}
        />
      </div>
    );
  }

  if (view === 'chat') {
    const selectedConv = CONVERSATIONS.find(c => c.id === selectedChatId);
    return (
      <ChatScreen
        chatId={selectedChatId}
        localUserId={user?.id ?? 'local-user'}
        remoteUserId={`remote-${selectedChatId}`}
        remoteUserName={selectedConv?.name ?? 'Unknown'}
        onBack={() => setView('list')}
        onViewProfile={() => setView('profile')}
        onOpenSettings={() => { setSettingsFrom('chat'); setView('settings'); }}
      />
    );
  }

  return (
    <ChatList
      onOpenChat={(chatId) => { setSelectedChatId(chatId); setView('chat'); }}
      onOpenSearch={() => setView('search')}
      onNewConversation={() => setView('new-conversation')}
      onOpenContacts={() => setView('contacts')}
      onOpenMyProfile={() => setView('my-profile')}
      onOpenSettings={() => { setSettingsFrom('list'); setView('settings'); }}
      onOpenNotifications={() => setView('notifications')}
    />
  );
}